import InfoCard from "@pages/tablet/residences/components/InfoCard"
import useStore from "@state/store"
import styled from "styled-components"

interface ICompareProps {
  selectedRows: any[]
}

export default function CompareScreen(props: ICompareProps) {
  const { selectedRows } = props
  const {
    compareActiveDuplexFloorplan,
    compareMode,
    compareSection,
    activeView,
    activeView2,
  } = useStore((state) => ({
    compareActiveDuplexFloorplan: state.compareActiveDuplexFloorplan,
    compareMode: state.compareMode,
    compareSection: state.compareSection,
    activeView: state.activeView,
    activeView2: state.activeView2,
  }))
  const exceptions = ["PH16A", "PH16D", "PH16E", "PH17A"]

  const isDuplex = (unit: any) => {
    return (
      unit?.unitType?.startsWith("TH") ||
      (unit?.unitType?.startsWith("PH") && !exceptions.includes(unit?.unitType))
    )
  }

  if (!compareMode || !selectedRows) return null

  return (
    <Wrapper
      selectedCompareUnit1={selectedRows[0]}
      selectedCompareUnit2={selectedRows[1]}
    >
      <div className="floorplan1">
        <div className={`no-unit ${selectedRows[0] === null ? "visible" : ""}`}>
          Select unit to compare
        </div>
        {compareSection === "floorplan" && (
          <div className="compare-unit">
            <div className="residence-details">
              <div className="info-card">
                <InfoCard selectedUnit={selectedRows[0]} />
              </div>

              <div className="floorplate">
                {selectedRows[0]?.unitType && (
                  <img
                    alt="Floorplan"
                    src={`/assets/images/residences/floorplans/${selectedRows[0]?.unitType}-P${isDuplex(selectedRows[0]) ? "-" + compareActiveDuplexFloorplan[0] : ""}.png`}
                  />
                )}
              </div>
            </div>
            <div className="unit-floorplan">
              <div
                className={`duplex ${isDuplex(selectedRows[0]) ? "show" : "hide"}`}
              >
                <span
                  className={
                    compareActiveDuplexFloorplan[0] === "UPPER" ? "active" : ""
                  }
                >
                  Duplex &#x2192; {compareActiveDuplexFloorplan[0]}
                </span>
              </div>
              {selectedRows[0]?.unitType && (
                <img
                  src={`/assets/images/residences/floorplans/${selectedRows[0]?.unitType}-F${isDuplex(selectedRows[0]) ? "-" + compareActiveDuplexFloorplan[0] : ""}.png`}
                  alt="Floorplan 1"
                />
              )}
            </div>
          </div>
        )}
        {compareSection === "view" && (
          <div className="compare-unit">
            <img
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              className="img"
              src={`/assets/images/residences/views/${activeView}`}
              alt="Floorplan 2"
            />
          </div>
        )}
      </div>
      <div className="floorplan2">
        <div className={`no-unit ${selectedRows[1] === null ? "visible" : ""}`}>
          Select unit to compare
        </div>
        {compareSection === "floorplan" && (
          <div className="compare-unit">
            <div className="residence-details">
              <div className="info-card">
                <InfoCard selectedUnit={selectedRows[1]} />
              </div>

              <div className="floorplate">
                {selectedRows[1]?.unitType && (
                  <img
                    alt="Floorplan"
                    src={`/assets/images/residences/floorplans/${selectedRows[1]?.unitType}-P${isDuplex(selectedRows[1]) ? "-" + compareActiveDuplexFloorplan[1] : ""}.png`}
                  />
                )}
              </div>
            </div>
            <div className="unit-floorplan">
              {selectedRows[1]?.unitType && (
                <img
                  src={`/assets/images/residences/floorplans/${selectedRows[1]?.unitType}-F${isDuplex(selectedRows[1]) ? "-" + compareActiveDuplexFloorplan[1] : ""}.png`}
                  alt="Floorplan 2"
                />
              )}
              <div
                className={`duplex ${isDuplex(selectedRows[1]) ? "show" : "hide"}`}
              >
                <span
                  className={
                    compareActiveDuplexFloorplan[1] === "UPPER" ? "active" : ""
                  }
                >
                  Duplex &#x2192; {compareActiveDuplexFloorplan[1]}
                </span>
              </div>
            </div>
          </div>
        )}
        {compareSection === "view" && (
          <div className="compare-unit">
            <img
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              className="img"
              src={`/assets/images/residences/views/${activeView2}`}
              alt="Floorplan 2"
            />
          </div>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{
  selectedCompareUnit1: any
  selectedCompareUnit2: any
}>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .floorplan1,
  .floorplan2 {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-top: none;
    overflow: hidden;
    transition: all 0.6s;

    .compare-unit {
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .residence-details {
      height: 20%;
      padding: 1em;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba(0, 0, 0, 0.2);

      .info-card {
        height: 100%;
        width: 100%;
        // need to override the InfoCard component styles
        // to make it fit the compare unit
        > *:first-child {
          padding: 0;
        }
      }

      .floorplate {
        height: 100%;
        width: 100%;
        padding: 1em;
        display: flex;
        justify-content: flex-end;

        img {
          object-fit: contain;
          height: 100%;
        }
      }

      .unit-number {
        font-size: 7em;
      }

      .floor {
        margin: 60px 0px 0px;
      }

      .unit-info {
        transform: scale(0.9);
        transform-origin: left;
      }

      .separator-root[data-orientation="vertical"] {
        height: 100%;
        width: 0.5px;
        margin: 0 10px;
        background-color: #000;
      }
    }

    .unit-floorplan {
      position: relative;
      height: 80%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 5em;

      img {
        object-fit: contain;
        width: 100%;
        height: 100%;
      }

      .duplex {
        display: flex;
        position: absolute;
        top: 0;
        left: -1px;

        &.show {
          opacity: 1;
          margin-left: 0em;
          pointer-events: auto;
          transition: all 0.3s ease-in-out;
        }

        &.hide {
          opacity: 0;
          margin-left: -5em;
          pointer-events: none;
          transition: all 0.3s ease-in-out;
        }

        span {
          text-transform: uppercase;
          background-color: rgba(0, 0, 0, 0.8);
          color: white;
          font-size: 1.3em;
          padding: 0.5em 1em;
          border: 1px solid #000;
          transition: all 0.3s ease-in-out;
        }
      }
    }
  }

  .floorplan1 {
    border-left: none;
  }
  .floorplan2 {
    margin-left: -1px;
  }

  .no-unit {
    text-transform: uppercase;
    position: absolute;
    top: 50%;
    left: 50%;
    opacity: 0;
    transform: translate(-50%, -50%);
    transition: opacity 0s ease-in;

    &.visible {
      opacity: 1;
      transition: opacity 0.6s ease-in;
    }
  }

  .floorplan1 > .compare-unit {
    opacity: ${(props) => (props.selectedCompareUnit1 ? 1 : 0)};
    transform: ${(props) =>
      props.selectedCompareUnit1 ? "scale(1)" : "scale(0)"};
    transition: opacity 0.6s;
  }

  .floorplan2 > .compare-unit {
    opacity: ${(props) => (props.selectedCompareUnit2 ? 1 : 0)};
    transform: ${(props) =>
      props.selectedCompareUnit2 ? "scale(1)" : "scale(0)"};
    transition: opacity 0.6s;
  }
`
